import { useCallback, useEffect, useRef, useState } from 'react'
import {
  PHOTO_TRAY_FOCUS_OPEN_DELAY_MS,
  PHOTO_TRAY_HOVER_OPEN_DELAY_MS,
  type PhotoTrayPreviewOpenSource,
  photoTrayPreviewCloseDelayMs,
  shouldKeepPhotoTrayPreviewOpen,
} from '@/lib/photos/photo-tray-hover-preview'

function clearTimeoutRef(ref: { current: number | null }) {
  if (ref.current == null) return
  window.clearTimeout(ref.current)
  ref.current = null
}

/**
 * The one tray preview that is currently open. Each card owns its own preview state, so without
 * this a hovered card and a keyboard-focused card can both be open at once (WMPCM-11008: only the
 * actively hovered or focused thumbnail's preview is visible at a time). The preview that opens
 * last wins and closes the previous one.
 *
 * Module-level on purpose: it is app-wide, shared by every tray that uses this hook (Photos,
 * ClikArt, Backgrounds), so a preview in one panel closes one left open in another.
 */
let activePreview: { owner: object; close: () => void } | null = null

export function usePhotoTrayHoverPreview(enabled: boolean) {
  const [open, setOpen] = useState(false)
  const openTimerRef = useRef<number | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const pointerOverTriggerRef = useRef(false)
  const pointerOverContentRef = useRef(false)
  const focusedRef = useRef(false)
  const openSourceRef = useRef<PhotoTrayPreviewOpenSource | null>(null)
  const ownerRef = useRef({})

  const releaseActive = useCallback(() => {
    if (activePreview?.owner === ownerRef.current) activePreview = null
  }, [])

  const clearTimers = useCallback(() => {
    clearTimeoutRef(openTimerRef)
    clearTimeoutRef(closeTimerRef)
  }, [])

  const closeNow = useCallback(() => {
    clearTimers()
    pointerOverTriggerRef.current = false
    pointerOverContentRef.current = false
    focusedRef.current = false
    openSourceRef.current = null
    setOpen(false)
    releaseActive()
  }, [clearTimers, releaseActive])

  useEffect(() => {
    if (!enabled) closeNow()
  }, [closeNow, enabled])

  useEffect(
    () => () => {
      clearTimers()
      releaseActive()
    },
    [clearTimers, releaseActive]
  )

  const scheduleOpen = useCallback(
    (source: PhotoTrayPreviewOpenSource, delay: number) => {
      clearTimeoutRef(closeTimerRef)
      clearTimeoutRef(openTimerRef)
      openTimerRef.current = window.setTimeout(() => {
        openTimerRef.current = null
        if (activePreview && activePreview.owner !== ownerRef.current) activePreview.close()
        activePreview = { owner: ownerRef.current, close: closeNow }
        openSourceRef.current = source
        setOpen(true)
      }, delay)
    },
    [closeNow]
  )

  const scheduleClose = useCallback(
    (delay: number) => {
      clearTimeoutRef(openTimerRef)

      const run = () => {
        closeTimerRef.current = null
        if (
          shouldKeepPhotoTrayPreviewOpen({
            pointerOverTrigger: pointerOverTriggerRef.current,
            pointerOverContent: pointerOverContentRef.current,
            focused: focusedRef.current,
          })
        ) {
          return
        }

        openSourceRef.current = null
        setOpen(false)
        releaseActive()
      }

      if (delay <= 0) {
        clearTimeoutRef(closeTimerRef)
        run()
        return
      }

      clearTimeoutRef(closeTimerRef)
      closeTimerRef.current = window.setTimeout(run, delay)
    },
    [releaseActive]
  )

  const onTriggerPointerEnter = useCallback(
    (pointerType: string) => {
      if (!enabled || pointerType === 'touch') return
      pointerOverTriggerRef.current = true
      scheduleOpen('pointer', PHOTO_TRAY_HOVER_OPEN_DELAY_MS)
    },
    [enabled, scheduleOpen]
  )

  const onTriggerPointerLeave = useCallback(() => {
    pointerOverTriggerRef.current = false
    if (focusedRef.current) return
    scheduleClose(photoTrayPreviewCloseDelayMs(openSourceRef.current))
  }, [scheduleClose])

  const onTriggerFocus = useCallback(() => {
    if (!enabled) return
    focusedRef.current = true
    scheduleOpen('focus', PHOTO_TRAY_FOCUS_OPEN_DELAY_MS)
  }, [enabled, scheduleOpen])

  const onTriggerBlur = useCallback(() => {
    focusedRef.current = false
    scheduleClose(photoTrayPreviewCloseDelayMs(openSourceRef.current === 'focus' ? 'focus' : null))
  }, [scheduleClose])

  const onContentPointerEnter = useCallback(() => {
    pointerOverContentRef.current = true
    clearTimeoutRef(closeTimerRef)
  }, [])

  const onContentPointerLeave = useCallback(() => {
    pointerOverContentRef.current = false
    if (focusedRef.current) return
    scheduleClose(photoTrayPreviewCloseDelayMs(openSourceRef.current))
  }, [scheduleClose])

  return {
    open,
    onTriggerPointerEnter,
    onTriggerPointerLeave,
    onTriggerFocus,
    onTriggerBlur,
    onContentPointerEnter,
    onContentPointerLeave,
    closeNow,
  }
}
